<template>
  <div class="ma-4 px-3">
    <searchBar
      @add="openDialog(), (editMode = false)"
      @updateSearch="(v) => (search = v)"
    />
    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="headers"
            :search="search"
            :items="program_schemes"
            :loading="loading"
            fixed-header
            class="secondary row-height-50"
          >
            <template #[`item.name`]="{ item }">
              <span
                @click="viewProgramSchemes(item)"
                style="
                  cursor: pointer;
                  color: #4f4584;
                  text-decoration: underline;
                "
                >{{ item.name }}</span
              >
            </template>
            <template v-slot:[`item.credits_label`]="{ item }">
              <div
                :class="{
                  'credit-mismatch': !item.is_credits_matched,
                  'credit-match': item.is_credits_matched,
                }"
              >
                {{ item.credits_label }}
              </div>
            </template>

            <template #[`item.action`]="{ item }">
              <v-icon small class="mr-2" color="accent" @click="edit(item)">
                mdi-pencil
              </v-icon>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>
    <v-dialog v-model="copyDialog" persistent max-width="450">
      <v-card class="rounded-lg" elevation="2">
        <v-card-text class="pa-6">
          <div class="mb-6">
            <div class="d-flex align-center mb-3">
              <v-radio-group v-model="creationMode" hide-details class="mt-0">
                <v-radio value="new" color="primary">
                  <template v-slot:label>
                    <div class="d-flex align-center">
                      <v-icon small class="mr-2" color="primary"
                        >mdi-file-document-outline</v-icon
                      >
                      <span class="font-weight-medium">Create new</span>
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>
            </div>
          </div>
          <div>
            <div class="d-flex align-center mb-3">
              <v-radio-group v-model="creationMode" hide-details class="mt-0">
                <v-radio value="copy" color="primary">
                  <template v-slot:label>
                    <div class="d-flex align-center">
                      <v-icon small class="mr-2" color="primary"
                        >mdi-content-copy</v-icon
                      >
                      <span class="font-weight-medium"
                        >Copy programme scheme from</span
                      >
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>
            </div>
            <v-slide-y-transition>
              <div v-if="creationMode === 'copy'" class="ml-8">
                <v-autocomplete
                  v-model="copyProgram"
                  :items="published_program_schemes"
                  item-text="name"
                  dense
                  outlined
                  return-object
                  hide-details
                  prepend-inner-icon="mdi-magnify"
                  class="mt-2"
                >
                </v-autocomplete>
                <p class="text-caption text-medium-emphasis mt-2 mb-0">
                  Duplicate an existing programme scheme with all configurations
                </p>
              </div>
            </v-slide-y-transition>
          </div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            @click="copyDialog = false"
            class="px-4"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            depressed
            @click="handleProceed"
            :disabled="creationMode === 'copy' && !copyProgram"
            class="px-4"
          >
            Proceed
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialog" width="800" persistent scrollable>
      <formDialog
        v-if="dialog"
        @closeDialog="clearDialog"
        @create="createProgramScheme"
        @update="updateProgramScheme"
        :program_scheme="program_scheme"
        :programs="programs"
        :partners="partners"
        :edit="editMode"
      />
    </v-dialog>
  </div>
</template>

<script>
import formDialog from "@/components/scheme/program_scheme.vue";
import searchBar from "@/components/searchBarFilter.vue";
import { programSchemeHeaders } from "@/constants/headers";
import { durations } from "@/constants/enum";
export default {
  components: {
    formDialog,
    searchBar,
  },
  data() {
    return {
      durations: durations,
      headers: programSchemeHeaders,
      dialog: false,
      copyDialog: false,
      editMode: false,
      deleteDialog: false,
      searchProgram: null,
      searchBatch: null,
      search: "",
      program_schemes: [],
      programs: [],
      program_scheme: {},
      creationMode: "new",
      copyProgram: {},
      loading: false,
      partners: [],
    };
  },
  watch: {},
  computed: {
    published_program_schemes() {
      return this.program_schemes.filter((item) => item.status == "Published");
    },
  },
  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Programme Schemes" }]);
    this.initialise();
  },
  methods: {
    async initialise() {
      this.loading = true;

      const [schemesRes, programsRes, partnersRes] = await Promise.all([
        this.$axios.get("program_schemes"),
        this.$axios.get("programs"),
        this.$axios.get("partners"),
      ]);

      this.program_schemes = schemesRes.data.data.sort((a, b) => b.id - a.id);
      this.programs = programsRes.data.data.sort((a, b) => b.id - a.id);
      this.partners = partnersRes.data.data.sort((a, b) => b.id - a.id);

      this.loading = false;
    },

    async createProgramScheme(data) {
      if (data.is_copied) {
        delete data.id;
      }
      await this.$axios
        .post("program_schemes", { program_scheme: data })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Programme Scheme created successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    async updateProgramScheme(data) {
      await this.$axios
        .put("program_schemes/" + data.id, { program_scheme: data })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Programme Scheme updated successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },

    edit(item) {
      this.program_scheme = { ...item };
      this.editMode = true;
      this.dialog = true;
    },
    resetValues() {
      this.editMode = false;
      this.dialog = false;
      this.deleteDialog = false;
      this.program_scheme = {};
    },
    viewProgramSchemes(item) {
      this.$router.push({
        path: `/view_program_schemes`,
        query: {
          id: item.id,
          name: item.name,
        },
      });
    },
    handleProceed() {
      if (this.creationMode == "new") {
        this.copyDialog = false;
        this.dialog = true;
      }
      if (this.creationMode == "copy") {
        this.copyDialog = false;
        this.copyProgram.copy_id = this.copyProgram.id;
        this.copyProgram.id = null;
        this.copyProgram.is_copied = true;
        this.program_scheme = { ...this.copyProgram, status: "Draft" };
        this.editMode = true;
        this.dialog = true;
      }
    },
    openDialog() {
      if (this.program_schemes.length == 0) {
        this.copyDialog = false;
        this.dialog = true;
        return;
      }
      this.copyDialog = true;
      this.copyProgram = {};
    },
    clearDialog() {
      this.copyProgram.is_copied = false;
      this.copyProgram.copy_id = null;
      this.dialog = false;
    },
  },
};
</script>

<style>
.credit-mismatch {
  /* Red color for the text and background for emphasis */
  color: red;
  border-radius: 4px;
  font-weight: bold;
}

.credit-match {
  /* Green color for successful matching */
  color: #4caf50; /* Green */
  font-weight: bold;
}
</style>
